import { Injectable, computed, inject } from '@angular/core';
import { Firestore, collection, doc, getDocs, getDoc, query, serverTimestamp, setDoc, updateDoc, where, onSnapshot } from '@angular/fire/firestore';
import { Auth, updateProfile, user } from '@angular/fire/auth';
import { Storage, ref, uploadBytes, getDownloadURL } from '@angular/fire/storage';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable, of, switchMap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { BillingProfile } from '../models/billing.model';
import { LEGAL_VERSION, OwnerSettings, PayoutMode, UserProfile, UserRole } from '../models/user.model';
import { isBillingProfileComplete, parseBillingProfile } from '../shared/billing/billing-profile.utils';
import { FirebaseAwareService } from '../shared/firestore/firebase-aware.service';
import { PROFILE_AVATAR_CACHE_CONTROL } from '../shared/storage/storage-cache.constants';

/** Audit fields written when the user accepts the legal terms at signup. */
export interface LegalAcceptance {
  legalAccepted:   true;
  legalVersion:    string;
}

@Injectable({ providedIn: 'root' })
export class UserService extends FirebaseAwareService {
  private firestore = inject(Firestore);
  private auth      = inject(Auth);
  private storage   = inject(Storage);
  private functions = inject(Functions);

  /** Reactive stream: resolves to UserProfile when logged in, null otherwise */
  private readonly _profile$: Observable<UserProfile | null> = user(this.auth).pipe(
    switchMap(authUser => {
      if (!authUser) return of(null);
      return new Observable<UserProfile | null>(subscriber => {
        const unsub = this.inCtx(() =>
          onSnapshot(
            doc(this.firestore, 'users', authUser.uid),
            snap  => subscriber.next(snap.exists() ? (snap.data() as UserProfile) : null),
            error => subscriber.error(error),
          )
        );
        return unsub;
      });
    }),
  );

  readonly currentProfile = toSignal(this._profile$, { initialValue: null });
  readonly role            = computed(() => this.currentProfile()?.role ?? null);
  readonly isOwner         = computed(() => {
    const r = this.role();
    return r === 'owner' || r === 'admin';
  });

  /**
   * Live signal — true when the owner has at least one payment method
   * enabled (SEPA / Bizum / Efectivo). Returns `null` while the profile
   * is still loading so consumers can avoid a flash of "no methods" UI.
   *
   * Uses the same legacy fallback as `getOwnerSettings`: when a toggle
   * is missing on the doc, infer from the presence of its data fields.
   * Cash has no associated data — strict `false` fallback (opt-in only).
   */
  /** True when billingProfile is complete — required for owners to publish. */
  readonly billingProfileComplete = computed<boolean | null>(() => {
    const p = this.currentProfile();
    if (!p) return null;
    return isBillingProfileComplete(parseBillingProfile(p.billingProfile) ?? null);
  });

  readonly paymentsActive = computed<boolean | null>(() => {
    const p = this.currentProfile() as (UserProfile & Record<string, unknown>) | null;
    if (!p) return null;
    const iban = typeof p['iban'] === 'string' ? p['iban'].trim() : '';
    const mobilePhone = typeof p['mobilePhone'] === 'string' ? p['mobilePhone'].trim() : '';
    const sepaEnabled = typeof p['sepaEnabled'] === 'boolean'
      ? p['sepaEnabled'] as boolean
      : iban.length > 0;
    const bizumEnabled = typeof p['bizumEnabled'] === 'boolean'
      ? p['bizumEnabled'] as boolean
      : mobilePhone.length > 0;
    const cashEnabled = typeof p['cashEnabled'] === 'boolean'
      ? p['cashEnabled'] as boolean
      : false;
    return sepaEnabled || bizumEnabled || cashEnabled;
  });

  /**
   * Creates the Firestore user document on first sign-in.
   * Idempotent — does nothing if the document already exists.
   *
   * Pass `legal` on the owner-signup path to bind the legal acceptance to
   * the new account at creation time (audit log).
   */
  async ensureUserProfile(
    uid:         string,
    email:       string | null,
    displayName: string | null,
    photoURL:    string | null,
    defaultRole: UserRole = 'owner',
    legal?:      LegalAcceptance,
  ): Promise<void> {
    const ref  = this.inCtx(() => doc(this.firestore, 'users', uid));
    const snap = await this.inCtx(() => getDoc(ref));
    if (!snap.exists()) {
      const profile: UserProfile & Record<string, unknown> = {
        uid,
        email:       email       ?? '',
        displayName: displayName ?? '',
        photoURL:    photoURL    ?? '',
        role:        defaultRole,
        createdAt:   Date.now(),
      };
      if (legal) {
        profile['legalAccepted']   = legal.legalAccepted;
        profile['legalVersion']    = legal.legalVersion ?? LEGAL_VERSION;
        // serverTimestamp() is the source-of-truth for proof; client clock
        // is not trustworthy for legal audit.
        profile['legalAcceptedAt'] = serverTimestamp();
      }
      await this.inCtx(() => setDoc(ref, profile));
    }
  }

  updateRole(uid: string, role: UserRole): Promise<void> {
    return this.inCtx(() =>
      updateDoc(doc(this.firestore, 'users', uid), { role }),
    );
  }

  /** Uploads a profile photo to Storage and returns its public download URL. */
  async uploadProfilePhoto(uid: string, file: File): Promise<string> {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const safeExt = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext) ? ext : 'jpg';
    const storageRef = this.inCtx(() =>
      ref(this.storage, `users/${uid}/avatar/photo.${safeExt}`),
    );
    await this.inCtx(() => uploadBytes(storageRef, file, {
      contentType: file.type || 'image/jpeg',
      cacheControl: PROFILE_AVATAR_CACHE_CONTROL,
    }));
    return this.inCtx(() => getDownloadURL(storageRef));
  }

  /**
   * Updates display name and/or photo on Firestore and Firebase Auth so the
   * header avatar and profile page stay in sync.
   */
  async updateUserProfile(
    uid: string,
    patch: { displayName?: string; photoURL?: string },
  ): Promise<void> {
    const authUser = this.auth.currentUser;
    if (!authUser || authUser.uid !== uid) {
      throw new Error('Debes iniciar sesión para actualizar tu perfil.');
    }

    const firestorePatch: Partial<Pick<UserProfile, 'displayName' | 'photoURL'>> = {};
    if (patch.displayName !== undefined) {
      firestorePatch.displayName = patch.displayName.trim();
    }
    if (patch.photoURL !== undefined) {
      firestorePatch.photoURL = patch.photoURL;
    }
    if (Object.keys(firestorePatch).length === 0) {
      return;
    }

    await this.inCtx(() =>
      updateDoc(doc(this.firestore, 'users', uid), firestorePatch),
    );

    const authPatch: { displayName?: string; photoURL?: string | null } = {};
    if (patch.displayName !== undefined) {
      authPatch.displayName = firestorePatch.displayName;
    }
    if (patch.photoURL !== undefined) {
      authPatch.photoURL = patch.photoURL || null;
    }
    if (Object.keys(authPatch).length > 0) {
      await updateProfile(authUser, authPatch);
    }
  }

  /**
   * Upgrades a renter (`user`) to `owner` so they can publish spaces.
   * No-op when already owner or admin.
   */
  async promoteToOwner(uid: string): Promise<void> {
    const ref = this.inCtx(() => doc(this.firestore, 'users', uid));
    const snap = await this.inCtx(() => getDoc(ref));
    if (!snap.exists()) {
      return;
    }
    const role = (snap.data() as UserProfile).role;
    if (role === 'owner' || role === 'admin') {
      return;
    }
    await this.updateRole(uid, 'owner');
  }

  /**
   * Reads the owner settings fields (SEPA + Bizum + toggles) directly from
   * the user's profile document. Returns an empty partial if the document
   * does not exist yet.
   *
   * The toggles fall back to "enabled when the data is filled" for legacy
   * profiles written before `sepaEnabled` / `bizumEnabled` existed.
   */
  async getOwnerSettings(uid: string): Promise<Partial<OwnerSettings>> {
    const snap = await this.inCtx(() => getDoc(doc(this.firestore, 'users', uid)));
    if (!snap.exists()) return {};
    const data = snap.data() as Record<string, unknown>;
    const accountHolder = typeof data['accountHolder'] === 'string' ? data['accountHolder'] : '';
    const iban          = typeof data['iban']          === 'string' ? data['iban']          : '';
    const mobilePhone   = typeof data['mobilePhone']   === 'string' ? data['mobilePhone']   : '';
    const sepaEnabled   = typeof data['sepaEnabled']   === 'boolean'
      ? data['sepaEnabled']
      : iban.length > 0;
    const bizumEnabled  = typeof data['bizumEnabled']  === 'boolean'
      ? data['bizumEnabled']
      : mobilePhone.length > 0;
    // Cash has no associated data — pure toggle. Legacy profiles default
    // to `false` (cash flows outside the platform, must be opt-in).
    const cashEnabled   = typeof data['cashEnabled']   === 'boolean'
      ? data['cashEnabled']
      : false;
    return { accountHolder, iban, mobilePhone, sepaEnabled, bizumEnabled, cashEnabled };
  }

  /**
   * Merges owner settings (IBAN, phone, account holder) into the user's
   * profile document without overwriting unrelated fields.
   */
  updateOwnerSettings(uid: string, settings: Partial<OwnerSettings>): Promise<void> {
    return this.inCtx(() =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      updateDoc(doc(this.firestore, 'users', uid), settings as any),
    );
  }

  async getBillingProfile(uid: string): Promise<BillingProfile | null> {
    const snap = await this.inCtx(() => getDoc(doc(this.firestore, 'users', uid)));
    if (!snap.exists()) return null;
    return parseBillingProfile((snap.data() as { billingProfile?: unknown }).billingProfile);
  }

  updateBillingProfile(uid: string, profile: BillingProfile): Promise<void> {
    return this.inCtx(() =>
      updateDoc(doc(this.firestore, 'users', uid), {
        billingProfile: {
          ...profile,
          taxId: profile.taxId.replace(/[\s.-]/g, '').toUpperCase(),
          updatedAt: Date.now(),
        },
      }),
    );
  }

  updatePayoutMode(uid: string, mode: PayoutMode): Promise<void> {
    return this.inCtx(() =>
      updateDoc(doc(this.firestore, 'users', uid), { payout_mode: mode }),
    );
  }

  /**
   * Returns the number of asset documents owned by the given uid.
   * Used to gate the owner→user role switch (business integrity check).
   *
   * NOTE: Firestore Security Rules cannot perform cross-collection queries,
   * so this check cannot be enforced server-side via rules alone.
   * For production hardening, add a Cloud Function trigger on
   * /users/{uid} writes that rejects a role downgrade when assets exist.
   */
  async getOwnedAssetCount(uid: string): Promise<number> {
    const snap = await this.inCtx(() =>
      getDocs(query(collection(this.firestore, 'assets'), where('ownerId', '==', uid))),
    );
    return snap.size;
  }

  /**
   * Updates the owner's public slug via the `updateOwnerSlug` Cloud Function.
   * Server-side enforces syntax, reserved words, uniqueness and the
   * old-slug → new-slug atomic swap on `/owner_slugs/`.
   */
  async updateOwnerSlug(slug: string): Promise<{ slug: string; changed: boolean }> {
    const call = this.inCtx(() =>
      httpsCallable<{ slug: string }, { ok: true; slug: string; changed: boolean }>(
        this.functions, 'updateOwnerSlug',
      ),
    );
    const res = await call({ slug });
    return { slug: res.data.slug, changed: res.data.changed };
  }

  /** Stripe Connect Express onboarding / account update link for the signed-in owner. */
  async createConnectOnboardingLink(
    returnOrigin: string,
    linkType: 'account_onboarding' | 'account_update' = 'account_onboarding',
  ): Promise<string> {
    const call = this.inCtx(() =>
      httpsCallable<
        { returnOrigin?: string; linkType?: string },
        { url: string }
      >(this.functions, 'createConnectOnboardingLink'),
    );
    const { data } = await call({ returnOrigin, linkType });
    if (!data.url) {
      throw new Error('Stripe did not return an onboarding URL.');
    }
    return data.url;
  }

  /** Refreshes Connect status from Stripe API into Firestore (after onboarding return). */
  async syncStripeConnectStatus(): Promise<{
    stripeConnectStatus: 'none' | 'onboarding' | 'restricted' | 'active';
    stripeChargesEnabled: boolean;
    stripePayoutsEnabled: boolean;
  }> {
    const call = this.inCtx(() =>
      httpsCallable<
        Record<string, never>,
        {
          ok: true;
          stripeConnectStatus: 'none' | 'onboarding' | 'restricted' | 'active';
          stripeChargesEnabled: boolean;
          stripePayoutsEnabled: boolean;
        }
      >(this.functions, 'syncStripeConnectStatus'),
    );
    const { data } = await call({});
    return {
      stripeConnectStatus: data.stripeConnectStatus,
      stripeChargesEnabled: data.stripeChargesEnabled,
      stripePayoutsEnabled: data.stripePayoutsEnabled,
    };
  }

  /** Clears Stripe Connect linkage on the profile; forces payout_mode MANUAL. */
  async disconnectStripeConnect(): Promise<{ disconnected: boolean }> {
    const call = this.inCtx(() =>
      httpsCallable<Record<string, never>, { ok: true; disconnected: boolean }>(
        this.functions,
        'disconnectStripeConnect',
      ),
    );
    const { data } = await call({});
    return { disconnected: data.disconnected };
  }

}
